<template>
  <div class="ed-gh-cb">
    <p class="ed-gh-cb__msg">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import type { AuthSession } from '../../../shared/components/authSessionStore'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useMockupEditorStore } from '@/stores/mockupEditor'
import { resolveMarketingApiOrigin } from '@/utils/marketingApiOrigin'
import { uiLocaleToEditorRouterPath } from '@/router/editorUrlLocale'

const message = ref('…')
const router = useRouter()
const { setSession } = useEditorAuth()
const store = useMockupEditorStore()

function buildKeepFields(keep: boolean, keepDays: number): Pick<AuthSession, 'keepDays' | 'expiresAt'> {
  if (!keep) return {}
  const days = [7, 14, 30].includes(keepDays) ? keepDays : 30
  return {
    keepDays: days,
    expiresAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
  }
}

onMounted(async () => {
  if (import.meta.env.SSR) return
  const q = new URLSearchParams(globalThis.location.search)
  const code = (q.get('code') || '').trim()
  const state = (q.get('state') || '').trim()
  const saved = sessionStorage.getItem('github_oauth_state') || ''
  const redirectUri = sessionStorage.getItem('github_oauth_redirect_uri') || ''
  const keep = sessionStorage.getItem('github_oauth_keep') === '1'
  const keepDays = Number(sessionStorage.getItem('github_oauth_keep_days') || '0') || 0

  if (!code || !state || !saved || state !== saved || !redirectUri) {
    message.value = 'GitHub 授权无效或已过期，请关闭本页后重试。'
    return
  }

  sessionStorage.removeItem('github_oauth_state')
  sessionStorage.removeItem('github_oauth_redirect_uri')
  sessionStorage.removeItem('github_oauth_keep')
  sessionStorage.removeItem('github_oauth_keep_days')

  const apiBase = resolveMarketingApiOrigin()
  if (!apiBase) {
    message.value = 'GitHub 登录失败：未配置 API 地址。'
    return
  }

  try {
    const res = await fetch(`${apiBase}/api/auth/github`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, redirect_uri: redirectUri, keep, keepDays }),
    })
    if (!res.ok) {
      let msg = 'request_failed'
      try {
        const j = (await res.json()) as { message?: string; statusMessage?: string }
        msg = j.statusMessage || j.message || msg
      } catch {
        /* ignore */
      }
      throw new Error(msg)
    }
    const data = (await res.json()) as {
      token: string
      refreshToken: string
      email: string
      username: string
      nickname: string
      avatar: string
      createdAt: string
      isSubscribed: boolean
    }
    setSession({
      token: data.token,
      refreshToken: data.refreshToken,
      email: data.email,
      username: data.username,
      nickname: data.nickname,
      avatar: data.avatar,
      createdAt: data.createdAt,
      isSubscribed: data.isSubscribed,
      ...buildKeepFields(keep, keepDays),
    })
    await router.replace(uiLocaleToEditorRouterPath(store.locale))
  } catch {
    message.value = 'GitHub 登录失败，请关闭本页后重试。'
  }
})
</script>

<style scoped>
.ed-gh-cb {
  min-height: 40vh;
  display: grid;
  place-items: center;
  padding: 2rem;
}
.ed-gh-cb__msg {
  margin: 0;
  font-size: 0.95rem;
  color: #334155;
}
</style>
